/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';
import { useHistory } from 'react-router-dom';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';

import actionTypes from 'constants/actionTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { TradingOffer } from 'types/TradingOffer';
import { Partner } from 'types';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { addTradingOffer } from 'modules/trading/redux/tradingOfferActions';
import { collectPublisherAuditContext } from 'modules/trading/utils/collectPublisherAuditContext';
import { updatePartner } from 'modules/partners/redux/partnerActions';

import {
  PublishTradingOfferDraft,
  PublishTradingOfferModalContextType,
  PublisherPartner,
} from '../types/PublishTradingOfferModalContextType';
import { publishTradingOfferModalInitValue } from '../constants/publishTradingOfferModalInitValue';
import { useGetValidityDays } from '../hooks/useGetValidityDates';
import { buildInitialPublisherPartner } from '../utils/buildInitialPublisherPartner';

export const PublishTradingOfferModalContext =
  createContext<PublishTradingOfferModalContextType>(
    publishTradingOfferModalInitValue
  );

export const PublishTradingOfferModalProvider: FC<{
  children: ReactNode;
  tradingOffer: PublishTradingOfferDraft;
  modalActionType?: string;
  size?: string;
  publisherPartner?: Partial<Partner> | null;
}> = ({
  children,
  tradingOffer,
  modalActionType = actionTypes.ADD,
  publisherPartner: publisherPartnerProp = null,
}) => {
  const { t } = useTranslate();
  const history = useHistory();

  const user = useSelector((state: { user?: { _id?: string } }) => state.user);

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] =
    useState<boolean>(false);
  const [isHideSellerName, setIsHideSellerName] = useState<boolean>(false);
  const [emailSubsidiary, setEmailSubsidiary] = useState<string>(
    tradingOffer.emailSubsidiary || ''
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [publishedOffer, setPublishedOffer] = useState<TradingOffer | null>(
    null
  );
  const [publisherDocuments, setPublisherDocuments] = useState<File[]>([]);
  const [editablePublisherPartner, setEditablePublisherPartner] =
    useState<PublisherPartner | null>(() =>
      buildInitialPublisherPartner(publisherPartnerProp, tradingOffer)
    );

  const { data: validityDaysData } = useGetValidityDays({
    startDate: tradingOffer.startDate,
    endDate: tradingOffer.endDate,
  });

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 2));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleChangeTermsAndConditionsAcceptance = () => {
    setIsTermsAndConditionsAccepted((prev) => !prev);
  };

  const handleChangeHideSellerName = () => {
    setIsHideSellerName((prev) => !prev);
  };

  const handleAddPublisherDocument = (file: File) => {
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (file.size > maxSize) {
      errorSwal(t('FileTooLargeMax5MB'));
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      errorSwal(t('InvalidFileTypePDFJPGPNG'));
      return;
    }
    setPublisherDocuments((prev) => [...prev, file]);
  };

  const handleRemovePublisherDocument = (index: number) => {
    setPublisherDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePublisherPartnerField = (field: string, value: string) => {
    setEditablePublisherPartner((prev) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  const handleUpdateEmailSubsidiary = (value: string) => {
    setEmailSubsidiary(value);
  };

  const handleUpdatePublisherPartnerAddressField = (
    field: string,
    value: string
  ) => {
    setEditablePublisherPartner((prev) => {
      if (!prev) return null;
      const currentAddress = prev.address || {
        line1: '',
        zip: '',
        city: '',
        state: '',
        country: '',
      };
      return {
        ...prev,
        address: { ...currentAddress, [field]: value },
      };
    });
  };

  const resolvePartnerId = (): string | undefined => {
    if (editablePublisherPartner?._id) {
      return editablePublisherPartner._id;
    }
    const { partner } = tradingOffer;
    if (!partner) return undefined;
    if (typeof partner === 'string') return partner;
    return partner._id;
  };

  const resolveSocietyId = (): string => {
    const { society } = tradingOffer;
    return typeof society === 'string' ? society : '';
  };

  const handlePublishTradingOffer = async () => {
    const partnerId = resolvePartnerId();
    const societyId = resolveSocietyId();

    if (!partnerId || !editablePublisherPartner) {
      errorSwal(t('ErrorPublishingTradingOffer'));
      return;
    }

    try {
      setIsLoading(true);

      const publisherDocumentIds: string[] = [];

      for (const file of publisherDocuments) {
        const fileType =
          fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user?._id,
          society: societyId,
          fileType,
          category: documentTypes.DOCUMENTS,
          subcategory: 'IDENTITY',
          description: t('IdentityDocumentForTradingOffer'),
        };
        const uploadedDoc = await store.dispatch(addDocument(newDocument));
        if (uploadedDoc?._id) {
          publisherDocumentIds.push(uploadedDoc._id);
        }
      }

      const priorDocuments = Array.isArray(tradingOffer.documents)
        ? tradingOffer.documents.filter(
            (id): id is string => typeof id === 'string'
          )
        : [];

      await store.dispatch(
        updatePartner({
          partnerId,
          updatePartnerData: {
            name: editablePublisherPartner.name,
            cif: editablePublisherPartner.cif,
            email: editablePublisherPartner.email,
            phone: editablePublisherPartner.phone,
            bankAccountNumber: editablePublisherPartner.bankAccountNumber,
            address: editablePublisherPartner.address,
            userId: user?._id || '',
            society: societyId,
          },
        })
      );

      const dispatchResult = (await store.dispatch(
        addTradingOffer({
          ...tradingOffer,
          emailSubsidiary,
          documents: [...priorDocuments, ...publisherDocumentIds],
          isSellerNameHidden: isHideSellerName,
          publisherAuditContext: collectPublisherAuditContext(),
        })
      )) as TradingOffer | undefined;

      setIsLoading(false);

      if (modalActionType === actionTypes.EDIT) {
        handleCloseModal();
        history.push(`/ofertas/${tradingOffer.society}`);
        return;
      }

      const state = store.getState() as { tradingOffers?: TradingOffer[] };
      const createdOffer = dispatchResult ?? state.tradingOffers?.[0];

      if (createdOffer) {
        setPublishedOffer(createdOffer);
      } else {
        handleCloseModal();
        history.push(`/ofertas/${tradingOffer.society}`);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingOffer')
          : t('ErrorPublishingTradingOffer');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <PublishTradingOfferModalContext.Provider
      value={{
        data: {
          modalActionType,
          tradingOffer,
          publishedOffer,
          validityDays: validityDaysData.validityDays,
          isTermsAndConditionsAccepted,
          isHideSellerName,
          isLoading,
          currentStep,
          publisherPartner: editablePublisherPartner,
          publisherDocuments,
          emailSubsidiary,
        },
        actions: {
          handleCloseModal,
          handleNextStep,
          handlePrevStep,
          handleChangeTermsAndConditionsAcceptance,
          handleChangeHideSellerName,
          handleAddPublisherDocument,
          handleRemovePublisherDocument,
          handleUpdatePublisherPartnerField,
          handleUpdatePublisherPartnerAddressField,
          handleUpdateEmailSubsidiary,
          handlePublishTradingOffer,
        },
      }}
    >
      {children}
    </PublishTradingOfferModalContext.Provider>
  );
};

export const usePublishTradingOfferModal = () => {
  const context = useContext(PublishTradingOfferModalContext);
  if (!context) {
    throw new Error(
      'usePublishTradingOfferModal must be used within a PublishTradingOfferModalProvider'
    );
  }
  return context;
};
